import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AreaId, PersonId, SubAreaId } from '@shared/brand';
import type { Person } from '@domain/entities/Person';
import { seedColorForSubArea } from '@domain/entities/Organization';
import { getServices } from '@infrastructure/services';
import { Avatar, Banner, Chip, EmptyState, Spinner, cx } from '../../design-system';
import { useToast } from '../../providers/ToastProvider';
import { WizardShell, wizardStyles as w } from '../../wizard/WizardShell';
import styles from './AreaTransferWizard.module.css';

/**
 * Traspaso de una persona de un área a otra.
 *
 * **Es un asistente y no un desplegable** porque cambiar de área no es cambiar un
 * campo: los líderes anteriores dejan de poder aprobarle —un líder de otra área no
 * puede leer la solicitud que firmaría—, la sub área deja de existir, y lo que
 * tenga sin decidir lo tienen personas que ya no decidirán. Cada uno de esos tres
 * es una decisión que alguien tiene que tomar mirándola, no un efecto colateral.
 *
 * Los pasos van en el orden en que las decisiones dependen unas de otras: primero a
 * dónde, porque de eso salen las sub áreas y los líderes posibles; y el resumen al
 * final enseña **lo que se va a deshacer** antes de deshacerlo, que es la parte que
 * no tiene vuelta.
 *
 * Lo aplica una sola llamada. Ver `transferPersonArea`: a trozos, un fallo a mitad
 * deja a alguien con el área nueva y los líderes viejos.
 */

const STEPS = [
  { label: 'Área destino' },
  { label: 'Sub área' },
  { label: 'Líderes' },
  { label: 'Resumen' },
] as const;

export const AreaTransferWizard = ({
  person,
  onClose,
  onDone,
}: {
  readonly person: Person;
  readonly onClose: () => void;
  readonly onDone: () => Promise<void>;
}): ReactNode => {
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [toAreaId, setToAreaId] = useState<AreaId | null>(null);
  const [toSubAreaId, setToSubAreaId] = useState<SubAreaId | null>(null);
  const [leaderIds, setLeaderIds] = useState<readonly PersonId[]>([]);
  const [busy, setBusy] = useState(false);

  const areas = useQuery({
    queryKey: ['transfer', 'areas'],
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  /* Las sub áreas y la gente del destino, sólo cuando ya hay destino: pedirlas
     antes sería una consulta por cada área que el usuario toca de paso. */
  const subAreas = useQuery({
    queryKey: ['transfer', 'subAreas', toAreaId],
    enabled: toAreaId !== null,
    queryFn: async () => {
      if (toAreaId === null) return [];
      const result = await getServices().organization.listSubAreas(toAreaId);
      return result.ok ? result.value : [];
    },
  });

  const destinationPeople = useQuery({
    queryKey: ['transfer', 'people', toAreaId],
    enabled: toAreaId !== null,
    queryFn: async () => {
      if (toAreaId === null) return [] as readonly Person[];
      const result = await getServices().people.list(
        { areaId: toAreaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok ? result.value.items : ([] as readonly Person[]);
    },
  });

  /*
   * Quién puede firmarle allí.
   *
   * Sólo quien ya tiene un rol que aprueba: nombrar líder a alguien *y* asignárselo
   * en el mismo movimiento sería hacer dos cambios de organización con un clic, y el
   * segundo quedaría sin que nadie lo hubiera decidido.
   *
   * Son los de `DECIDING_ROLES` en el servidor, y `global` entró ahí el 28 sep 2026:
   * quien lo tiene dirige un equipo, y dejarlo fuera de esta lista era no poder
   * asignarle a nadie al mover a una persona a su área.
   */
  const leaderCandidates = useMemo(
    () =>
      (destinationPeople.data ?? []).filter(
        (p) =>
          p.id !== person.id &&
          (p.role === 'lider' || p.role === 'admin' || p.role === 'global' || p.role === 'super'),
      ),
    [destinationPeople.data, person.id],
  );

  /* Lo que el traspaso va a cancelar, para poder decirlo antes y no después. */
  const inFlight = useQuery({
    queryKey: ['transfer', 'inFlight', person.id],
    queryFn: async () => {
      const services = getServices();
      const [vacations, permits, reports] = await Promise.all([
        services.requests.list(
          { personId: person.id, statuses: ['Por revisar', 'En revisión', 'Visto bueno'] },
          { limit: 50 },
        ),
        services.permissions.list(
          { personId: person.id, statuses: ['Por aprobar'] },
          { limit: 50 },
        ),
        services.leaders.reportsOf(person.id),
      ]);
      return {
        vacations: vacations.ok ? vacations.value.items.length : 0,
        permits: permits.ok ? permits.value.items.length : 0,
        /* A quién lidera hoy: gente que se queda sin ese líder al trasladarlo. */
        reports: reports.ok ? new Set(reports.value.map((link) => link.personId)).size : 0,
      };
    },
  });

  const toArea = (areas.data ?? []).find((a) => a.id === toAreaId);
  /* El nombre, no el identificador: «se queda en tecnologia» es una fuga de la base
     de datos a la cara de quien decide. */
  const fromAreaName =
    (areas.data ?? []).find((a) => a.id === person.areaId)?.name ?? 'su área actual';
  const toSubArea = (subAreas.data ?? []).find((s) => s.id === toSubAreaId);

  const toggleLeader = (id: PersonId): void => {
    setLeaderIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  };

  const advance = (): void => {
    if (step === 0) {
      if (toAreaId === null) {
        toast.warn('Elige el área a la que se traslada.');
        return;
      }
      setStep(1);
      return;
    }
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      /*
       * Sin líder sólo cuando no hay a quién elegir.
       *
       * Un área recién creada está vacía y no tiene a quién nombrar, así que
       * exigirlo siempre haría imposible el primer traslado a ella. Con candidatos
       * a la vista, dejarlo en blanco sí es un descuido y se bloquea.
       */
      if (leaderIds.length === 0 && leaderCandidates.length > 0) {
        toast.warn('Elige al menos un líder: si no, sus solicitudes recaen en quien administre el área.');
        return;
      }
      setStep(3);
      return;
    }
    void submit();
  };

  const submit = async (): Promise<void> => {
    if (toAreaId === null) return;
    setBusy(true);
    const result = await getServices().people.transferArea({
      personId: person.id,
      toAreaId,
      toSubAreaId,
      leaderPersonIds: leaderIds,
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad(`No se pudo trasladar. ${describe(result.error)}`);
      return;
    }

    const { cancelledVacations, cancelledPermits, releasedReports } = result.value;
    const cancelled = cancelledVacations + cancelledPermits;
    const donde = toArea?.name ?? 'su área nueva';
    toast.ok(
      cancelled === 0
        ? `${person.displayName} ahora está en ${donde}.`
        : cancelled === 1
          ? `${person.displayName} ahora está en ${donde}. Se canceló 1 solicitud sin decidir; tendrá que volver a pedirla.`
          : `${person.displayName} ahora está en ${donde}. Se cancelaron ${String(
              cancelled,
            )} solicitudes sin decidir; tendrá que volver a pedirlas.`,
    );
    /* Aparte, porque es trabajo para quien traslada y no un dato del traslado. */
    if (releasedReports > 0) {
      toast.warn(
        releasedReports === 1
          ? `1 persona de ${fromAreaName} se quedó sin ese líder: nómbrale otro.`
          : `${String(releasedReports)} personas de ${fromAreaName} se quedaron sin ese líder: nómbrales otro.`,
      );
    }
    await onDone();
    onClose();
  };

  const pendingCount = (inFlight.data?.vacations ?? 0) + (inFlight.data?.permits ?? 0);
  const reportCount = inFlight.data?.reports ?? 0;

  return (
    <WizardShell
      eyebrow="Traspaso de área"
      title={`Trasladar a ${person.displayName}`}
      steps={STEPS}
      current={step}
      nextLabel={step === 3 ? 'Trasladar →' : 'Continuar →'}
      busy={busy}
      onBack={() => {
        setStep((s) => Math.max(0, s - 1));
      }}
      onNext={advance}
      onClose={onClose}
    >
      {step === 0 && (
        <>
          <h2 className={w.stepTitle}>¿A qué área se traslada?</h2>
          <p className={w.stepNote}>
            Hoy está en {fromAreaName}. Al cambiar de área, sus líderes actuales dejan de
            poder aprobarle.
          </p>

          {areas.isLoading ? (
            <Spinner label="Cargando áreas" />
          ) : (
            <div className={styles.areaList}>
              {(areas.data ?? [])
                .filter((a) => a.id !== person.areaId && a.active)
                .map((area) => {
                  const picked = area.id === toAreaId;
                  return (
                    <button
                      key={area.id}
                      type="button"
                      className={cx(styles.areaCard, picked && styles.areaCardPicked)}
                      aria-pressed={picked}
                      onClick={() => {
                        setToAreaId(area.id);
                        /* Lo elegido en el área anterior no vale en ésta. */
                        setToSubAreaId(null);
                        setLeaderIds([]);
                      }}
                    >
                      <span className={styles.areaName}>{area.name}</span>
                      <Chip
                        background={picked ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'}
                        foreground={picked ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'}
                      >
                        {picked ? 'Elegida' : 'Elegir'}
                      </Chip>
                    </button>
                  );
                })}
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <>
          <h2 className={w.stepTitle}>¿En qué sub área queda?</h2>
          <p className={w.stepNote}>
            La sub área fija cuántas personas pueden estar fuera a la vez. Puede quedarse
            sin asignar y ponerse después.
          </p>

          {subAreas.isLoading ? (
            <Spinner label="Cargando sub áreas" />
          ) : (subAreas.data ?? []).length === 0 ? (
            <EmptyState
              glyph="◇"
              title="Esa área no tiene sub áreas"
              note="Puedes trasladarla igual y asignarle una cuando existan."
            />
          ) : (
            <div className={styles.areaList}>
              <button
                type="button"
                className={cx(styles.areaCard, toSubAreaId === null && styles.areaCardPicked)}
                aria-pressed={toSubAreaId === null}
                onClick={() => {
                  setToSubAreaId(null);
                }}
              >
                <span className={styles.areaName}>Sin asignar</span>
              </button>
              {(subAreas.data ?? []).map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  className={cx(styles.areaCard, sub.id === toSubAreaId && styles.areaCardPicked)}
                  aria-pressed={sub.id === toSubAreaId}
                  onClick={() => {
                    setToSubAreaId(sub.id);
                  }}
                >
                  <span className={styles.areaName}>{sub.name}</span>
                  <span className={styles.areaNote}>máx. {sub.maxConcurrentOut} fuera</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {step === 2 && (
        <>
          <h2 className={w.stepTitle}>¿Quién le firma allí?</h2>
          <p className={w.stepNote}>
            Obligatorio: un líder de otra área no puede leer la solicitud que aprobaría, así
            que los actuales dejan de servir. Con dos o más, sus vacaciones piden doble visto
            bueno.
          </p>

          {destinationPeople.isLoading ? (
            <Spinner label="Cargando personas" />
          ) : leaderCandidates.length === 0 ? (
            <Banner tone="warn">
              {toArea?.name ?? 'Esa área'} todavía no tiene a nadie con rol de líder — es lo
              normal en un área que se está estrenando. Puedes continuar: {person.displayName}{' '}
              entrará sin quien le firme —se las tendrá que firmar quien administre el área— y
              aparecerá en «Personas sin líder» en Inicio hasta que nombres uno.
            </Banner>
          ) : (
            <div className={styles.leaderList}>
              {leaderCandidates.map((candidate) => {
                const on = leaderIds.includes(candidate.id);
                return (
                  <button
                    key={candidate.id}
                    type="button"
                    className={cx(styles.leaderRow, on && styles.leaderRowOn)}
                    aria-pressed={on}
                    onClick={() => {
                      toggleLeader(candidate.id);
                    }}
                  >
                    <Avatar
                      name={candidate.displayName}
                      color={seedColorForSubArea(candidate.displayName)}
                      size={34}
                    />
                    <span className={styles.leaderText}>
                      <span className={styles.leaderName}>{candidate.displayName}</span>
                      <span className={styles.areaNote}>{candidate.email}</span>
                    </span>
                    <Chip
                      background={on ? 'var(--color-success-bg)' : 'var(--color-surface-subtle-alt)'}
                      foreground={on ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'}
                    >
                      {on ? 'Le firma' : 'Elegir'}
                    </Chip>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {step === 3 && (
        <>
          <h2 className={w.stepTitle}>Revisa antes de trasladar</h2>
          <p className={w.stepNote}>Esto no se deshace solo.</p>

          <dl className={styles.summary}>
            <div className={styles.summaryRow}>
              <dt className={styles.summaryKey}>Persona</dt>
              <dd className={styles.summaryValue}>{person.displayName}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.summaryKey}>Área</dt>
              <dd className={styles.summaryValue}>
                {fromAreaName} → {toArea?.name ?? '—'}
              </dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.summaryKey}>Sub área</dt>
              <dd className={styles.summaryValue}>{toSubArea?.name ?? 'Sin asignar'}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.summaryKey}>Le firman</dt>
              <dd className={styles.summaryValue}>
                {leaderIds.length === 0
                  ? 'Nadie todavía'
                  : leaderCandidates
                      .filter((c) => leaderIds.includes(c.id))
                      .map((c) => c.displayName)
                      .join(' · ')}
              </dd>
            </div>
          </dl>

          {/* Lo que se deshace, dicho antes. Un resumen que sólo enseña el estado
              final esconde justamente la parte que no tiene vuelta. */}
          <div className={styles.undoBand}>
            <p className={styles.undoTitle}>Lo que este traslado deshace</p>
            <ul className={styles.undoList}>
              <li>Sus líderes actuales dejan de serlo.</li>
              {reportCount > 0 && (
                <li>
                  <strong>
                    Deja de liderar a {reportCount} {reportCount === 1 ? 'persona' : 'personas'} de{' '}
                    {fromAreaName}.
                  </strong>{' '}
                  Desde otra área no puede firmarles. A quien no tenga otro líder le firmará
                  quien administre {fromAreaName} hasta que le nombres uno.
                </li>
              )}
              {pendingCount > 0 ? (
                <li>
                  <strong>
                    {pendingCount} {pendingCount === 1 ? 'solicitud' : 'solicitudes'} sin decidir
                  </strong>{' '}
                  {pendingCount === 1
                    ? 'se cancela y los días vuelven a su saldo. Tendrá que volver a pedirla.'
                    : 'se cancelan y los días vuelven a su saldo. Tendrá que volver a pedirlas.'}
                </li>
              ) : (
                <li>No tiene nada esperando decisión.</li>
              )}
              <li>
                Su historial de ausencias se queda en {fromAreaName}: lo que ocurrió ahí
                siguió ocurriendo ahí. Su saldo sí viaja.
              </li>
              {leaderIds.length === 0 && (
                <li>
                  {/* Decía que no podría pedir días, y nunca fue cierto: nada en el
                      envío mira los líderes. Lo que sí cambia es quién le firma. */}
                  <strong>Entra sin líder.</strong> Podrá pedir días igual, y se los firmará
                  quien administre {toArea?.name ?? 'su área nueva'} hasta que le nombres uno.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </WizardShell>
  );
};

const describe = (error: { kind: string } & Record<string, unknown>): string => {
  const what = typeof error['what'] === 'string' ? error['what'] : '';
  switch (error.kind) {
    case 'permission-denied':
      return what.length > 0 ? what : 'Tu perfil no puede trasladar personas entre áreas.';
    case 'invalid':
    case 'conflict':
    case 'not-found':
      return what;
    default:
      return 'Intenta de nuevo en un momento.';
  }
};
